import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  Cpu, HardDrive, MemoryStick, Database, Activity, Clock,
  RefreshCw, Wifi, Server, AlertTriangle, CheckCircle2,
  ChevronDown, ChevronUp, Gauge, Cloud
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatUptime(seconds) {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function getStatusColor(percent) {
  if (percent >= 90) return { bg: 'bg-red-500', text: 'text-red-400', ring: 'ring-red-500/30', bar: 'bg-red-500' };
  if (percent >= 75) return { bg: 'bg-amber-500', text: 'text-amber-400', ring: 'ring-amber-500/30', bar: 'bg-amber-500' };
  return { bg: 'bg-emerald-500', text: 'text-emerald-400', ring: 'ring-emerald-500/30', bar: 'bg-emerald-500' };
}

function GaugeRing({ percent, label, sublabel, icon: Icon, size = 120 }) {
  const colors = getStatusColor(percent);
  const radius = (size - 16) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percent / 100) * circumference;

  return (
    <div className="flex flex-col items-center" data-testid={`gauge-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#1e1e3a" strokeWidth="8" />
          <circle
            cx={size/2} cy={size/2} r={radius} fill="none"
            stroke="currentColor"
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className={`${colors.text} transition-all duration-700 ease-out`}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <Icon className={`w-5 h-5 ${colors.text} mb-0.5`} />
          <span className={`text-lg font-bold ${colors.text}`}>{percent}%</span>
        </div>
      </div>
      <p className="text-sm font-medium text-white mt-2">{label}</p>
      {sublabel && <p className="text-xs text-gray-500">{sublabel}</p>}
    </div>
  );
}

function StatRow({ label, value, sub }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-[#2d2d44]/50 last:border-0">
      <span className="text-sm text-gray-400">{label}</span>
      <div className="text-right">
        <span className="text-sm font-medium text-white">{value}</span>
        {sub && <p className="text-xs text-gray-500">{sub}</p>}
      </div>
    </div>
  );
}

export default function ServerMonitor({ isDarkMode }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(10);
  const [showCollections, setShowCollections] = useState(false);
  const [history, setHistory] = useState([]);
  const intervalRef = useRef(null);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/server/stats`);
      const data = response.data;
      setStats(data);
      setError(null);
      setHistory(prev => {
        const next = [...prev, { time: new Date(), cpu: data.cpu.percent, mem: data.memory.percent, disk: data.disk.percent }];
        return next.slice(-30); // Keep last 30 data points
      });
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Access denied. Super admin only.');
      } else {
        setError('Failed to fetch server stats');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchStats, refreshInterval * 1000);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoRefresh, refreshInterval, fetchStats]);

  const needsUpgrade = stats && (stats.cpu.percent >= 80 || stats.memory.percent >= 80 || stats.disk.percent >= 85);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 text-[#6D4AFF] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="bg-[#1a1a2e] border-red-500/30">
        <CardContent className="p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Access Restricted</h3>
          <p className="text-gray-400">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (!stats) return null;

  return (
    <div className="space-y-6" data-testid="server-monitor">
      {/* Header Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${autoRefresh ? 'bg-emerald-400 animate-pulse' : 'bg-gray-500'}`} />
            <span className="text-xs text-gray-400">
              {autoRefresh ? `Live — every ${refreshInterval}s` : 'Paused'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={refreshInterval}
            onChange={(e) => setRefreshInterval(Number(e.target.value))}
            className="bg-[#252540] border border-[#2d2d44] text-white text-xs rounded-md px-2 py-1.5"
            data-testid="refresh-interval-select"
          >
            <option value={5}>5s</option>
            <option value={10}>10s</option>
            <option value={30}>30s</option>
            <option value={60}>60s</option>
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`border-[#2d2d44] text-xs ${autoRefresh ? 'text-emerald-400' : 'text-gray-400'}`}
            data-testid="toggle-auto-refresh"
          >
            {autoRefresh ? 'Pause' : 'Resume'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchStats}
            className="border-[#2d2d44] text-gray-400"
            data-testid="manual-refresh-btn"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Upgrade Alert */}
      {needsUpgrade && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-start gap-3" data-testid="upgrade-alert">
          <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-amber-300 font-medium text-sm">Resource usage is high</p>
            <p className="text-amber-400/70 text-xs mt-1">
              One or more resources exceed 80%. Consider upgrading your server instance for better performance.
            </p>
          </div>
        </div>
      )}

      {/* Gauges Row */}
      <Card className="bg-[#1a1a2e] border-[#2d2d44]">
        <CardContent className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 justify-items-center">
            <GaugeRing
              percent={stats.cpu.percent}
              label="CPU"
              sublabel={`${stats.cpu.cores} cores`}
              icon={Cpu}
            />
            <GaugeRing
              percent={stats.memory.percent}
              label="Memory"
              sublabel={`${stats.memory.used_gb} / ${stats.memory.total_gb} GB`}
              icon={MemoryStick}
            />
            <GaugeRing
              percent={stats.disk.percent}
              label="Disk"
              sublabel={`${stats.disk.used_gb} / ${stats.disk.total_gb} GB`}
              icon={HardDrive}
            />
            <GaugeRing
              percent={Math.min(100, Math.round((stats.process.memory_mb / (stats.memory.total_gb * 1024)) * 100))}
              label="App Memory"
              sublabel={`${stats.process.memory_mb} MB`}
              icon={Server}
            />
          </div>
        </CardContent>
      </Card>

      {/* Mini Sparkline History */}
      {history.length > 1 && (
        <Card className="bg-[#1a1a2e] border-[#2d2d44]">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-sm flex items-center gap-2">
              <Activity className="w-4 h-4 text-[#6D4AFF]" />
              Resource History (last {history.length} readings)
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-4">
            <div className="space-y-3">
              {[
                { key: 'cpu', label: 'CPU', color: '#6D4AFF' },
                { key: 'mem', label: 'RAM', color: '#00bcd4' },
                { key: 'disk', label: 'Disk', color: '#f59e0b' }
              ].map(({ key, label, color }) => (
                <div key={key} className="flex items-center gap-3">
                  <span className="text-xs text-gray-500 w-10">{label}</span>
                  <div className="flex-1 flex items-end gap-px h-6">
                    {history.map((h, i) => (
                      <div
                        key={i}
                        className="flex-1 rounded-t-sm transition-all duration-300"
                        style={{
                          height: `${Math.max(2, h[key])}%`,
                          backgroundColor: color,
                          opacity: 0.3 + (i / history.length) * 0.7,
                        }}
                      />
                    ))}
                  </div>
                  <span className="text-xs text-gray-400 w-10 text-right">
                    {history[history.length - 1]?.[key]}%
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detail Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* System Info */}
        <Card className="bg-[#1a1a2e] border-[#2d2d44]">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-sm flex items-center gap-2">
              <Server className="w-4 h-4 text-[#6D4AFF]" />
              System
            </CardTitle>
          </CardHeader>
          <CardContent>
            <StatRow label="CPU Cores" value={stats.cpu.cores} />
            {stats.cpu.freq_mhz && <StatRow label="CPU Freq" value={`${stats.cpu.freq_mhz} MHz`} />}
            <StatRow label="System Uptime" value={formatUptime(stats.uptime.system_seconds)} />
            <StatRow label="App Uptime" value={formatUptime(stats.uptime.app_seconds)} />
            <StatRow label="App PID" value={stats.process.pid} />
            <StatRow label="Threads" value={stats.process.threads} />
          </CardContent>
        </Card>

        {/* Network */}
        <Card className="bg-[#1a1a2e] border-[#2d2d44]">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-sm flex items-center gap-2">
              <Wifi className="w-4 h-4 text-[#00bcd4]" />
              Network I/O
            </CardTitle>
          </CardHeader>
          <CardContent>
            <StatRow label="Data Sent" value={`${stats.network.bytes_sent_mb} MB`} />
            <StatRow label="Data Received" value={`${stats.network.bytes_recv_mb} MB`} />
            <StatRow label="RAM Free" value={`${stats.memory.available_gb} GB`} />
            <StatRow label="Disk Free" value={`${stats.disk.free_gb} GB`} />
          </CardContent>
        </Card>
      </div>

      {/* MongoDB Stats */}
      <Card className="bg-[#1a1a2e] border-[#2d2d44]">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white text-sm flex items-center gap-2">
              <Database className="w-4 h-4 text-emerald-400" />
              MongoDB — {stats.mongodb.db_name}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">
                {stats.mongodb.collections} collections
              </Badge>
              <Badge className="bg-[#6D4AFF]/20 text-[#6D4AFF] text-xs">
                {stats.mongodb.documents?.toLocaleString()} docs
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="bg-[#252540] rounded-lg p-3 text-center">
              <p className="text-xs text-gray-500">Data Size</p>
              <p className="text-lg font-bold text-white">{stats.mongodb.data_size_mb} MB</p>
            </div>
            <div className="bg-[#252540] rounded-lg p-3 text-center">
              <p className="text-xs text-gray-500">Storage</p>
              <p className="text-lg font-bold text-white">{stats.mongodb.storage_size_mb} MB</p>
            </div>
            <div className="bg-[#252540] rounded-lg p-3 text-center">
              <p className="text-xs text-gray-500">Indexes</p>
              <p className="text-lg font-bold text-white">{stats.mongodb.index_size_mb} MB</p>
            </div>
            <div className="bg-[#252540] rounded-lg p-3 text-center">
              <p className="text-xs text-gray-500">Total</p>
              <p className="text-lg font-bold text-white">{stats.mongodb.total_size_mb} MB</p>
            </div>
          </div>

          {/* Collections Toggle */}
          {stats.mongodb.collections_detail && (
            <div>
              <button
                onClick={() => setShowCollections(!showCollections)}
                className="flex items-center gap-2 text-sm text-[#6D4AFF] hover:text-[#8B5CF6] transition-colors"
                data-testid="toggle-collections"
              >
                {showCollections ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showCollections ? 'Hide' : 'Show'} collection breakdown
              </button>

              {showCollections && (
                <div className="mt-3 bg-[#252540] rounded-lg overflow-hidden">
                  <div className="grid grid-cols-4 gap-2 px-4 py-2 border-b border-[#2d2d44] text-xs text-gray-500 font-medium">
                    <span>Collection</span>
                    <span className="text-right">Documents</span>
                    <span className="text-right">Size</span>
                    <span className="text-right">Avg Doc</span>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {stats.mongodb.collections_detail.map((coll) => (
                      <div key={coll.name} className="grid grid-cols-4 gap-2 px-4 py-2 border-b border-[#2d2d44]/30 text-sm hover:bg-[#2d2d44]/30">
                        <span className="text-gray-300 truncate font-mono text-xs">{coll.name}</span>
                        <span className="text-right text-white">{coll.documents.toLocaleString()}</span>
                        <span className="text-right text-gray-400">{coll.size_mb} MB</span>
                        <span className="text-right text-gray-400">{coll.avg_doc_size} B</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* iDrive E2 Storage */}
      <Card className="bg-[#1a1a2e] border-[#2d2d44]">
        <CardHeader className="pb-2">
          <CardTitle className="text-white text-sm flex items-center gap-2">
            <Cloud className="w-4 h-4 text-cyan-400" />
            iDrive E2 Cloud Storage
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats.idrive?.configured ? (
            <div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="bg-[#252540] rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Total Objects</p>
                  <p className="text-lg font-bold text-white">{stats.idrive.total_objects?.toLocaleString()}</p>
                </div>
                <div className="bg-[#252540] rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Space Used</p>
                  <p className="text-lg font-bold text-white">
                    {stats.idrive.used_gb >= 1
                      ? `${stats.idrive.used_gb} GB`
                      : `${stats.idrive.used_mb} MB`}
                  </p>
                </div>
                <div className="bg-[#252540] rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Bucket</p>
                  <p className="text-sm font-medium text-cyan-400 truncate">{stats.idrive.bucket}</p>
                </div>
                <div className="bg-[#252540] rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Endpoint</p>
                  <p className="text-xs font-medium text-gray-400 truncate">{stats.idrive.endpoint?.replace('https://', '')}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <Cloud className="w-8 h-8 text-gray-600 mx-auto mb-2" />
              <p className="text-sm text-gray-500">
                {stats.idrive?.error ? 'Failed to connect to iDrive' : 'iDrive E2 not configured'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Timestamp */}
      <p className="text-xs text-gray-600 text-center">
        Last updated: {new Date(stats.timestamp).toLocaleString()}
      </p>
    </div>
  );
}
